import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaPlus, FaTrash, FaCheck, FaWrench, FaFilePdf, FaSpinner, FaExclamationCircle, FaCamera, FaInfoCircle } from 'react-icons/fa';
import { useFinalizarMantenimiento } from '@/hooks/useFinalizarMantenimiento';
import AutocompleteSelect from '@/components/common/AutocompleteSelect';
import Badge from '@/components/common/Badge';
import Modal from '@/components/common/Modal';
import PatenteDisplay from '@/components/common/PatenteDisplay';
import { usePlanValidation } from '@/hooks/usePlanValidation';
import QuotaWarning from '@/components/common/QuotaWarning';
import PaymentModal from '@/components/payments/PaymentModal';
import { ImageUploadManager } from '@/components/images';
import ImageGalleryWithViewer from '@/components/images/ImageGalleryWithViewer';
import CitaImagenesApiService from '@/services/citaImagenesApiService';
import MantenimientoImagenesApiService from '@/services/mantenimientoImagenesApiService';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { useSucursal } from '@/context/SucursalContext';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-hot-toast';
import PDFGenerator from '@/components/common/PDFGenerator';
import * as mantenimientosApiService from '@/services/mantenimientosApiService';

const FinalizarMantenimientoModal = ({ isOpen, onClose, cita, onSuccess }) => {
  // Usar el hook personalizado para manejar la lógica del formulario
  const {
    formData,
    setFormData,
    servicios,
    repuestos,
    mecanicos,
    ivaPorcentaje,
    isSubmitting,
    errors,
    handleServicioChange,
    handleRepuestoChange,
    handleItemAdicionalChange,
    handleAddServicio,
    handleAddRepuesto,
    handleAddItemAdicional,
    handleRemoveServicio,
    handleRemoveRepuesto,
    handleRemoveItemAdicional,
    handleSubmit: submitHandler,
    handleGeneratePDF,
    resetFormData
  } = useFinalizarMantenimiento(cita, () => {
    // Limpiar formulario después del éxito
    resetFormData();
    if (onSuccess) onSuccess();
  });

  // Hook para validación de cuotas
  const {
    validateQuota,
    getResourceUsage,
    showPaymentModal,
    targetPlanId,
    closePaymentModal,
    openPaymentModal
  } = usePlanValidation();

  // Hooks adicionales para images
  const { empresaActual } = useSimpleEmpresa();
  const { sucursalData } = useSucursal();
  const { session } = useSession();

  // Estado para manejo de imágenes
  const [imagenesDespues, setImagenesDespues] = useState([]);
  const [imagenesAntes, setImagenesAntes] = useState([]);
  const [loadingImagenesAntes, setLoadingImagenesAntes] = useState(false);
  const [uploadingImagenes, setUploadingImagenes] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Cargar imágenes previas cuando el modal se abre
  useEffect(() => {
    const cargarImagenesAntes = async () => {
      if (isOpen && cita?.id) {
        setLoadingImagenesAntes(true);
        try {
          const imagenesExistentes = await CitaImagenesApiService.obtenerImagenesCita(session, cita.id);
          setImagenesAntes(imagenesExistentes);
        } catch (error) {
          console.error('Error cargando imágenes anteriores:', error);
          // No mostrar error toast para no interferir con la UX
        } finally {
          setLoadingImagenesAntes(false);
        }
      }
    };

    cargarImagenesAntes();
  }, [isOpen, cita?.id]);

  // Limpiar formulario cuando el modal se cierre
  useEffect(() => {
    if (!isOpen) {
      resetFormData();
      setImagenesDespues([]);
      setImagenesAntes([]);
    }
  }, [isOpen]); // Eliminar resetFormData de las dependencias para evitar el loop

  // Manejar cambios en imágenes Posterior
  const handleImagenesDespuesChange = (nuevasImagenes) => {
    setImagenesDespues(nuevasImagenes);
  };

  // Manejador de envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar que existan fotos previas
    if (imagenesAntes.length === 0) {
      toast.error('Debe cargar fotos del estado previo del vehículo antes de finalizar el mantenimiento');
      return;
    }

    // Validar cuota de vehículos antes de crear
    const canCreateMantenimiento = validateQuota('mantenimientos', {
      showToast: false,
      showModal: true
    });
    
    if (!canCreateMantenimiento) {
      //console.log('🚫 No se puede crear mantenimiento - límite alcanzado');
      return; // Bloquear el envío del formulario
    }

    // Si hay imágenes Posterior, subirlas primero
    if (imagenesDespues.length > 0) {
      setUploadingImagenes(true);
      
      try {
        // Primero crear el mantenimiento para obtener el ID (sin generar PDF aún)
        const mantenimientoResult = await submitHandler(e, { skipImageUpload: true, skipPDFGeneration: true });
        
        if (mantenimientoResult?.id) {
          // Luego subir las imágenes POSTERIOR
          const imagenesSubidas = [];
          
          for (let i = 0; i < imagenesDespues.length; i++) {
            const imagen = imagenesDespues[i];
            
            try {
              const imagenSubida = await MantenimientoImagenesApiService.subirImagen(
                session,
                mantenimientoResult.id,
                empresaActual?.id,
                sucursalData?.id,
                imagen.compressedFile || imagen.originalFile,
                'despues',
                imagen.descripcion || '',
                i // Orden
              );
              
              imagenesSubidas.push(imagenSubida);
              
            } catch (error) {
              console.error(`Error subiendo imagen Posterior ${i + 1}:`, error);
              toast.error(`Error subiendo imagen: ${imagen.originalFile.name}`);
            }
          }
          
          if (imagenesSubidas.length > 0) {
            toast.success(`${imagenesSubidas.length} imagen${imagenesSubidas.length > 1 ? 'es' : ''} del resultado guardada${imagenesSubidas.length > 1 ? 's' : ''} correctamente`);
          }

          // Ahora generar el PDF con todas las imágenes (incluyendo las posteriores recién subidas)
          try {
            console.log('🔄 Generando PDF con imágenes posteriores...');
            const pdfResult = await handleGeneratePDF(mantenimientoResult);
            
            // Guardar PDF en la base de datos
            if (pdfResult?.base64) {
              await mantenimientosApiService.guardarPDFBase64(mantenimientoResult.id, pdfResult.base64);
              console.log('✅ PDF con imágenes posteriores guardado correctamente');
            }
          } catch (pdfError) {
            console.error('❌ Error generando PDF con imágenes posteriores:', pdfError);
            toast.warning('Mantenimiento finalizado, pero hubo un error generando el PDF con las imágenes posteriores');
          }
        }
        
      } catch (error) {
        console.error('Error en el proceso completo:', error);
        toast.error('Error al finalizar el mantenimiento con imágenes');
      } finally {
        setUploadingImagenes(false);
      }
    } else {
      // Sin imágenes posteriores, proceso normal
      await submitHandler(e);
    }
  };

  // Manejador para cerrar modal (también limpia el formulario)
  const handleClose = () => {
    if (uploadingImagenes) {
      const confirmar = window.confirm('¿Está seguro de cerrar? Se perderán las imágenes que se están subiendo.');
      if (!confirmar) return;
    }
    
    resetFormData();
    setImagenesDespues([]);
    setImagenesAntes([]);
    onClose();
  };

  // Obtener métricas de uso para mantenimientos
  const mantenimientosUsage = getResourceUsage('mantenimientos');

  if (!isOpen) return null;

  const modalTitle = (
    <div className="flex items-center gap-4 w-full">
      <div className="flex-1 min-w-0">
        <span>Finalizar Mantenimiento</span>
        <p className="text-gray-400 text-sm font-normal mt-1">
          Cita #{cita?.correlativo} • {cita?.fecha}
        </p>
      </div>
      <div className="flex items-center">
        <PatenteDisplay patente={cita?.vehiculo?.patente} size="sm" />
      </div>
    </div>
  );

  const modalFooter = (
    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
      {/* Texto de verificación - lado izquierdo */}
      <div className="text-xs text-gray-400 order-2 md:order-1">
        <p>Verifica que toda la información sea correcta antes de finalizar</p>
      </div>
      
      {/* Botones - lado derecho */}
      <div className="flex flex-col md:flex-row gap-3 order-1 md:order-2">
        {/* Botón cancelar - primero en mobile */}
        <button
          type="button"
          onClick={handleClose}
          className="w-full md:w-auto px-4 py-2.5 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 order-1 md:order-1"
        >
          Cancelar
        </button>
        
        {/* Botón secundario - Generar PDF */}
        <button
          type="button"
          onClick={async () => {
            // Generación local usando pdfmake con imágenes actuales del modal
            try {
              setGeneratingPdf(true);

              // Cargar configuración del taller
              const configTaller = await mantenimientosApiService.getConfiguracionTaller(empresaActual?.id);

              // Convertir imágenes 'después' cargadas (archivos locales) a base64
              const toBase64 = (file) => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
              });

              const imagenesDespuesItems = [];
              for (const img of imagenesDespues) {
                // Priorizar compressedFile si existe, si no, intentar originalFile
                const f = img?.compressedFile || img?.originalFile;
                if (f) {
                  try {
                    const b64 = await toBase64(f);
                    imagenesDespuesItems.push({ base64: b64, descripcion: img?.descripcion || null });
                  } catch (e) {
                    console.warn('No se pudo convertir imagen local a base64', e);
                  }
                }
              }

              // imágenes "antes" ya vienen del API (url/base64), se pasan directo
              const datosMantenimiento = {
                vehiculo: cita?.vehiculo || {},
                cliente: cita?.cliente || {},
                kilometrajeActual: formData.kilometrajeActual,
                observaciones: formData.observaciones,
                ivaPorcentaje: ivaPorcentaje,
                servicios: formData.serviciosRealizados,
                repuestos: formData.repuestosUtilizados,
                itemsAdicionales: formData.itemsAdicionales,
                mecanicos: mecanicos.filter(m => formData.mecanicosIds.includes(String(m.id))),
                imagenesAntes: imagenesAntes,
                imagenesDespues: imagenesDespuesItems,
              };

              const pdfGen = new PDFGenerator();
              pdfGen.setConfigTaller(configTaller || {});
              await pdfGen.generarPDFGarantiaPdfMake(datosMantenimiento);
            } catch (err) {
              console.error('Error generando PDF (pdfmake):', err);
              toast.error('No se pudo generar el PDF');
            } finally {
              setGeneratingPdf(false);
            }
          }}
          disabled={isSubmitting || generatingPdf}
          className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed order-2 md:order-2"
        >
          {isSubmitting || generatingPdf ? (
            <>
              <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4" />
              {generatingPdf ? 'Generando PDF...' : 'Procesando...'}
            </>
          ) : (
            <>
              <FaFilePdf className="-ml-1 mr-2 h-4 w-4" />
              Generar PDF
            </>
          )}
        </button>
        
        {/* Botón principal - Finalizar Mantenimiento - último en mobile */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || uploadingImagenes || imagenesAntes.length === 0 || loadingImagenesAntes}
          className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed order-3 md:order-3"
          title={imagenesAntes.length === 0 ? "Debe cargar fotos del estado previo para continuar" : ""}
        >
          {isSubmitting || uploadingImagenes ? (
            <>
              <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4" />
              {uploadingImagenes ? 'Subiendo fotos...' : 'Procesando...'}
            </>
          ) : (
            <>
              <FaCheck className="-ml-1 mr-2 h-4 w-4" />
              Finalizar Mantenimiento
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={modalTitle}
        size="2xl"
        footer={modalFooter}
      >

        <form onSubmit={handleSubmit} className="space-y-4">
          <QuotaWarning
            resourceType="mantenimientos"
            usageMetric={mantenimientosUsage}
            onUpgrade={() => openPaymentModal()}
            className="mb-6"
          />
          {/* Información de la Cita */}
          <div className="bg-gray-700/50 rounded-xl p-5 border border-gray-600/30">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full mr-2"></span>
              Información de la Cita
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-400 text-sm block mb-1">Cliente</span>
                <p className="text-white font-medium">{cita?.cliente?.nombre} {cita?.cliente?.apellido}</p>
                {cita?.cliente?.telefono && (
                  <p className="text-gray-400 text-xs mt-1">{cita.cliente.telefono}</p>
                )}
              </div>
              <div>
                <span className="text-gray-400 text-sm block mb-1">Vehículo</span>
                <p className="text-white font-medium">
                  {cita?.vehiculo?.marca_local || cita?.vehiculo?.marca || 'N/A'} {cita?.vehiculo?.modelo_local || cita?.vehiculo?.modelo || 'N/A'}
                </p>
                <p className="text-gray-400 text-xs">{cita?.vehiculo?.año || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm block mb-1">Motivo</span>
                <p className="text-white font-medium">{cita?.motivo || 'Mantenimiento preventivo'}</p>
              </div>
            </div>
          </div>

          {/* Mecánicos */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white">
              Mecánicos que Realizaron el Mantenimiento <span className="text-red-500">*</span>
            </label>
            <p className="text-gray-400 text-xs mb-3">Selecciona los mecánicos que participaron en el mantenimiento</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {mecanicos.length === 0 ? (
                <div className="col-span-2 text-center py-4 text-gray-400">
                  <p>Cargando mecánicos...</p>
                </div>
              ) : (
                mecanicos.map((mecanico) => {
                const isSelected = formData.mecanicosIds.includes(String(mecanico.id));
                return (
                  <div 
                    key={mecanico.id} 
                    className={`flex items-center space-x-3 p-3 rounded-xl cursor-pointer transition-all duration-200 ${
                      isSelected 
                        ? 'bg-blue-500/10 border border-blue-500/30' 
                        : 'bg-gray-700/50 border border-gray-600/30 hover:border-gray-500/50'
                    }`}
                    onClick={() => {
                      if (isSelected) {
                        setFormData(prev => ({
                          ...prev,
                          mecanicosIds: prev.mecanicosIds.filter(id => id !== String(mecanico.id))
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          mecanicosIds: [...prev.mecanicosIds, String(mecanico.id)]
                        }));
                      }
                    }}
                  >
                    <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                      isSelected 
                        ? 'bg-blue-500 border-blue-500' 
                        : 'bg-gray-600 border-gray-500'
                    }`}>
                      {isSelected && <FaCheck className="text-white text-[10px]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate">{mecanico.nombre}</div>
                      <div className="text-gray-400 text-sm">{mecanico.especialidad}</div>
                    </div>
                  </div>
                );
                })
              )}
            </div>
            
            {formData.mecanicosIds.length === 0 && (
              <p className="text-red-400 text-sm mt-2 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Debes seleccionar al menos un mecánico
              </p>
            )}
          </div>

          {/* Kilometraje Actual del Vehículo */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white">
              Kilometraje Actual del Vehículo <span className="text-red-500">*</span>
            </label>
            <p className="text-gray-400 text-xs">Ingresa el kilometraje actual según el odómetro del vehículo</p>
            
            <div className="relative w-full">
              <div className="relative">
                <input
                  type="number"
                  value={formData.kilometrajeActual}
                  onChange={(e) => setFormData({ ...formData, kilometrajeActual: e.target.value })}
                  className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 pl-4 pr-12 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="Ej: 45000"
                  min="0"
                  step="1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">km</span>
              </div>
              
              {errors.kilometrajeActual && (
                <p className="mt-2 text-sm text-red-400 flex items-center">
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.kilometrajeActual}
                </p>
              )}
            </div>
          </div>

          {/* Servicios Realizados */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full mr-2"></span>
                Servicios Realizados
              </h3>
              <button
                type="button"
                onClick={handleAddServicio}
                className="inline-flex items-center px-3.5 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <FaPlus className="mr-1.5" size={10} />
                Agregar Servicio
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.serviciosRealizados.length === 0 ? (
                <div className="bg-gray-700/30 border-2 border-dashed border-gray-600 rounded-xl p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h4 className="mt-2 text-sm font-medium text-gray-200">No hay servicios agregados</h4>
                  <p className="mt-1 text-xs text-gray-400">
                    {servicios.length === 0 ? 'Cargando servicios disponibles...' : 'Comienza agregando un servicio al mantenimiento'}
                  </p>
                  <button
                    type="button"
                    onClick={handleAddServicio}
                    disabled={servicios.length === 0}
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-600 bg-blue-600/10 hover:bg-blue-600/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaPlus className="mr-1.5" size={10} />
                    Agregar primer servicio
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.serviciosRealizados.map((servicio, index) => (
                    <div key={index} className="bg-gray-700/30 border border-gray-600/30 rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-white">
                              {servicio.nombre || 'Nuevo servicio'}
                            </h4>
                            {servicio.esServicioCita && (
                              <Badge variant="info" size="sm">
                                cita
                              </Badge>
                            )}
                          </div>
                          {servicio.descripcion && (
                            <p className="text-xs text-gray-400 mt-1">{servicio.descripcion}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
                            ${servicio.precio ? Number(servicio.precio).toLocaleString() : '0'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveServicio(index)}
                            className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                            title="Eliminar servicio"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Servicio
                          </label>
                          <AutocompleteSelect
                            options={servicios}
                            value={servicio.servicioId}
                            onChange={(selectedId) => handleServicioChange(index, 'servicioId', selectedId)}
                            placeholder={servicios.length === 0 ? 'Cargando servicios...' : 'Buscar servicio...'}
                            displayProperty="nombre"
                            valueProperty="id"
                            secondaryProperty="precio"
                            className="w-full"
                            disabled={servicios.length === 0}
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Precio ($)
                          </label>
                          <input
                            type="number"
                            value={servicio.precio || ''}
                            onChange={(e) => handleServicioChange(index, 'precio', e.target.value)}
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="0"
                            min="0"
                            step="100"
                          />
                        </div>
                        
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Observaciones (opcional)
                          </label>
                          <input
                            type="text"
                            value={servicio.observaciones || ''}
                            onChange={(e) => handleServicioChange(index, 'observaciones', e.target.value)}
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="Ej: Cambio de aceite sintético"
                          />
                        </div>
                      </div>

                      {/* Sección de Recordatorios */}
                      <div className="mt-4 pt-4 border-t border-gray-600/50">
                        <div className="flex items-center mb-3">
                          <input
                            type="checkbox"
                            id={`recordatorio-${index}`}
                            checked={servicio.recordatorioActivo || false}
                            onChange={(e) => handleServicioChange(index, 'recordatorioActivo', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                          />
                          <label htmlFor={`recordatorio-${index}`} className="ml-2 text-sm font-medium text-gray-300">
                            Programar próximo servicio
                          </label>
                        </div>

                        {servicio.recordatorioActivo && (
                          <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-400 mb-1">
                                  Tipo de recordatorio
                                </label>
                                <select
                                  value={servicio.tipoRecordatorio || 'kilometraje'}
                                  onChange={(e) => handleServicioChange(index, 'tipoRecordatorio', e.target.value)}
                                  className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                  <option value="kilometraje">Por kilometraje</option>
                                  <option value="fecha">Por fecha</option>
                                  <option value="ambos">Ambos criterios</option>
                                </select>
                              </div>

                              {(servicio.tipoRecordatorio === 'kilometraje' || servicio.tipoRecordatorio === 'ambos') && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-400 mb-1">
                                    Próximo servicio en (km)
                                  </label>
                                  <input
                                    type="number"
                                    value={servicio.proximoKilometraje || ''}
                                    onChange={(e) => handleServicioChange(index, 'proximoKilometraje', e.target.value)}
                                    className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ej: 10000"
                                    min="0"
                                  />
                                </div>
                              )}

                              {(servicio.tipoRecordatorio === 'fecha' || servicio.tipoRecordatorio === 'ambos') && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-400 mb-1">
                                    Próximo servicio (fecha)
                                  </label>
                                  <input
                                    type="date"
                                    value={servicio.proximaFecha || ''}
                                    onChange={(e) => handleServicioChange(index, 'proximaFecha', e.target.value)}
                                    className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  />
                                </div>
                              )}

                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-400 mb-1">
                                  Descripción del próximo servicio
                                </label>
                                <input
                                  type="text"
                                  value={servicio.descripcionProximoServicio || ''}
                                  onChange={(e) => handleServicioChange(index, 'descripcionProximoServicio', e.target.value)}
                                  className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="Ej: Cambio de aceite y filtro"
                                />
                              </div>

                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-400 mb-1">
                                  Observaciones para el mecánico
                                </label>
                                <textarea
                                  value={servicio.mecanicoObservaciones || ''}
                                  onChange={(e) => handleServicioChange(index, 'mecanicoObservaciones', e.target.value)}
                                  className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  rows="2"
                                  placeholder="Notas importantes para el próximo servicio..."
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddServicio}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      <FaPlus className="mr-1.5" size={10} />
                      Agregar otro servicio
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {errors.serviciosRealizados && (
              <p className="text-sm text-red-400 flex items-start mt-1">
                <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.serviciosRealizados}
              </p>
            )}
          </div>

          {/* Repuestos Utilizados */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="inline-block w-1.5 h-5 bg-amber-500 rounded-full mr-2"></span>
                Repuestos Utilizados
              </h3>
              <button
                type="button"
                onClick={handleAddRepuesto}
                className="inline-flex items-center px-3.5 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200"
              >
                <FaPlus className="mr-1.5" size={10} />
                Agregar Repuesto
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.repuestosUtilizados.length === 0 ? (
                <div className="bg-gray-700/30 border-2 border-dashed border-gray-600 rounded-xl p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <h4 className="mt-2 text-sm font-medium text-gray-200">No hay repuestos agregados</h4>
                  <p className="mt-1 text-xs text-gray-400">
                    {repuestos.length === 0 ? 'Cargando repuestos disponibles...' : 'Agrega los repuestos utilizados en el mantenimiento'}
                  </p>
                  <button
                    type="button"
                    onClick={handleAddRepuesto}
                    disabled={repuestos.length === 0}
                    className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-amber-600 bg-amber-600/10 hover:bg-amber-600/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FaPlus className="mr-1.5" size={10} />
                    Agregar primer repuesto
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.repuestosUtilizados.map((repuesto, index) => (
                    <div key={index} className="bg-gray-700/30 border border-gray-600/30 rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-sm font-medium text-white">
                            {repuesto.nombre || 'Nuevo repuesto'}
                          </h4>
                          {repuesto.codigo && (
                            <p className="text-xs text-gray-400 mt-0.5">Código: {repuesto.codigo}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400">
                            ${repuesto.precioUnitario ? (repuesto.precioUnitario * (repuesto.cantidad || 1)).toLocaleString() : '0'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveRepuesto(index)}
                            className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                            title="Eliminar repuesto"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Repuesto
                          </label>
                          <div className="relative">
                            <AutocompleteSelect
                              options={repuestos}
                              value={repuesto.repuestoId}
                              onChange={(selectedId) => handleRepuestoChange(index, 'repuestoId', selectedId)}
                              placeholder={repuestos.length === 0 ? 'Cargando repuestos...' : 'Buscar repuesto...'}
                              displayProperty="nombre"
                              valueProperty="id"
                              secondaryProperty="stock"
                              className={`w-full ${errors[`repuestos.${index}.repuestoId`] ? 'border-red-500 focus:ring-red-500' : ''}`}
                              disabled={repuestos.length === 0}
                            />
                            {errors[`repuestos.${index}.repuestoId`] && (
                              <p className="mt-1 text-xs text-red-400 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errors[`repuestos.${index}.repuestoId`]}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Precio Unitario ($)
                          </label>
                          <div>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={repuesto.precioUnitario || ''}
                              onChange={(e) => handleRepuestoChange(index, 'precioUnitario', e.target.value)}
                              className={`block w-full bg-gray-700/50 border ${errors[`repuestos.${index}.precioUnitario`] ? 'border-red-500 focus:ring-red-500' : 'border-gray-600/50 focus:ring-amber-500 focus:border-amber-500'} text-white text-base rounded-lg p-2 focus:ring-2 transition-colors duration-200`}
                              placeholder="Precio unitario"
                            />
                            {errors[`repuestos.${index}.precioUnitario`] && (
                              <p className="mt-1 text-xs text-red-400 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errors[`repuestos.${index}.precioUnitario`]}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Cantidad
                          </label>
                          <div>
                            <input
                              type="number"
                              min="1"
                              value={repuesto.cantidad || ''}
                              onChange={(e) => handleRepuestoChange(index, 'cantidad', e.target.value)}
                              className={`block w-full bg-gray-700/50 border ${errors[`repuestos.${index}.cantidad`] ? 'border-red-500 focus:ring-red-500' : 'border-gray-600/50 focus:ring-amber-500 focus:border-amber-500'} text-white text-base rounded-lg p-2 focus:ring-2 transition-colors duration-200`}
                              placeholder="Cantidad"
                            />
                            {errors[`repuestos.${index}.cantidad`] && (
                              <p className="mt-1 text-xs text-red-400 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                {errors[`repuestos.${index}.cantidad`]}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Observaciones (opcional)
                          </label>
                          <input
                            type="text"
                            value={repuesto.observaciones || ''}
                            onChange={(e) => handleRepuestoChange(index, 'observaciones', e.target.value)}
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors duration-200"
                            placeholder="Ej: Filtro de aceite marca Fram"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddRepuesto}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-amber-400 hover:text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-200"
                    >
                      <FaPlus className="mr-1.5" size={10} />
                      Agregar otro repuesto
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {errors.repuestosUtilizados && (
              <p className="text-sm text-red-400 flex items-start mt-1">
                <svg className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {errors.repuestosUtilizados}
              </p>
            )}
          </div>

          {/* Items Adicionales */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="inline-block w-1.5 h-5 bg-blue-500 rounded-full mr-2"></span>
                Ítems Adicionales
              </h3>
              <button
                type="button"
                onClick={handleAddItemAdicional}
                className="inline-flex items-center px-3.5 py-2 border border-transparent text-xs font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <FaPlus className="mr-1.5" size={10} />
                Agregar Ítem
              </button>
            </div>
            
            <div className="space-y-3">
              {formData.itemsAdicionales.length === 0 ? (
                <div className="bg-gray-700/30 border-2 border-dashed border-gray-600 rounded-xl p-6 text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <h4 className="mt-2 text-sm font-medium text-gray-200">No hay ítems adicionales</h4>
                  <p className="mt-1 text-xs text-gray-400">Agrega otros conceptos que no sean servicios ni repuestos</p>
                  <button
                    type="button"
                    onClick={handleAddItemAdicional}
                    className="mt-3 inline-flex items-center px-3.5 py-1.5 border border-transparent text-xs font-medium rounded-lg text-blue-600 bg-blue-600/10 hover:bg-blue-600/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    <FaPlus className="mr-1.5" size={10} />
                    Agregar primer ítem
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.itemsAdicionales.map((item, index) => (
                    <div key={index} className="bg-gray-700/30 border border-gray-600/30 rounded-xl p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="text-sm font-medium text-white">
                            {item.descripcion || 'Nuevo ítem'}
                          </h4>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400">
                            ${item.precio_unitario ? (item.precio_unitario * (item.cantidad || 1)).toLocaleString() : '0'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveItemAdicional(index)}
                            className="text-white bg-red-600 hover:bg-red-700 px-2 py-1 rounded"
                            title="Eliminar ítem"
                          >
                            <FaTrash size={12} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Descripción <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="text"
                            name="descripcion"
                            value={item.descripcion || ''}
                            onChange={(e) => handleItemAdicionalChange(index, 'descripcion', e.target.value)}
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="Ej: Descuento especial, recargo por urgencia, etc."
                          />
                          {errors?.[`itemsAdicionales[${index}].descripcion`] && (
                            <p className="mt-1 text-xs text-red-400 flex items-center">
                              <FaExclamationCircle className="mr-1" size={12} />
                              {errors[`itemsAdicionales[${index}].descripcion`].message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Precio Unitario <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            name="precio_unitario"
                            value={item.precio_unitario || ''}
                            onChange={(e) => handleItemAdicionalChange(index, 'precio_unitario', parseFloat(e.target.value) || 0)}
                            min="0"
                            step="1"
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="Precio unitario"
                          />
                          {errors?.[`itemsAdicionales[${index}].precio_unitario`] && (
                            <p className="mt-1 text-xs text-red-400 flex items-center">
                              <FaExclamationCircle className="mr-1" size={12} />
                              {errors[`itemsAdicionales[${index}].precio_unitario`].message}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Cantidad <span className="text-red-400">*</span>
                          </label>
                          <input
                            type="number"
                            name="cantidad"
                            value={item.cantidad}
                            onChange={(e) => handleItemAdicionalChange(index, 'cantidad', parseInt(e.target.value) || 1)}
                            min="1"
                            step="1"
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="Cantidad"
                          />
                          {errors?.[`itemsAdicionales[${index}].cantidad`] && (
                            <p className="mt-1 text-xs text-red-400 flex items-center">
                              <FaExclamationCircle className="mr-1" size={12} />
                              {errors[`itemsAdicionales[${index}].cantidad`].message}
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-4">
                          <label className="block text-xs font-medium text-gray-400 mb-1">
                            Observaciones
                          </label>
                          <input
                            type="text"
                            name="observaciones"
                            value={item.observaciones || ''}
                            onChange={(e) => handleItemAdicionalChange(index, 'observaciones', e.target.value)}
                            className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                            placeholder="Notas adicionales sobre este ítem"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddItemAdicional}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-400 hover:text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                    >
                      <FaPlus className="mr-1.5" size={10} />
                      Agregar otro ítem
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Observaciones */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white">
              Observaciones Adicionales
            </label>
            <p className="text-gray-400 text-xs mb-2">Agrega cualquier observación o detalle adicional sobre el mantenimiento realizado</p>
            <div className="relative">
              <textarea
                value={formData.observaciones}
                onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                rows={3}
                className="block w-full bg-gray-700/50 border border-gray-600/50 text-white text-base rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                placeholder="Ej: Se recomienda alineación y balanceo en el próximo servicio..."
                maxLength={500}
              />
              <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                {formData.observaciones?.length || 0}/500
              </div>
            </div>
          </div>

          {/* Fotos Estado Posterior */}
          <div className="bg-gray-700/50 rounded-xl p-5 border border-gray-600/30 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <span className="inline-block w-1.5 h-5 bg-green-500 rounded-full mr-2"></span>
                Fotos Estado Posterior
              </h3>
              <div className="flex items-center text-xs text-gray-400">
                <FaInfoCircle className="mr-1" />
                Opcional
              </div>
            </div>
            
            <p className="text-gray-400 text-sm">
              Documente el resultado del trabajo realizado. Estas fotos se incluirán en el reporte final.
            </p>

            {/* Validación de fotos previas */}
            {loadingImagenesAntes ? (
              <div className="text-center py-4">
                <FaSpinner className="animate-spin mx-auto text-blue-400 mb-2" size={20} />
                <p className="text-gray-400 text-sm">Verificando fotos del estado inicial...</p>
              </div>
            ) : imagenesAntes.length === 0 ? (
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center text-yellow-300">
                  <FaInfoCircle className="mr-2 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">Fotos del estado previo requeridas</p>
                    <p className="text-xs text-yellow-200 mt-1">
                      Debe cargar las fotos del estado inicial del vehículo usando el menú contextual de la cita antes de poder finalizar el mantenimiento.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Resumen de fotos previas */}
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center text-blue-300">
                      <FaCheck className="mr-2" />
                      <span className="text-sm">
                        ✓ {imagenesAntes.length} foto{imagenesAntes.length !== 1 ? 's' : ''} del estado previo ya cargada{imagenesAntes.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                  
                  {/* Galería compacta de imágenes previas */}
                  <ImageGalleryWithViewer
                    images={imagenesAntes}
                    title=""
                    showDeleteButton={false}
                    viewerTitle={`Fotos Estado Previo - ${cita?.vehiculo?.patente || 'Vehículo'}`}
                    className="bg-transparent border-none p-0"
                  />
                </div>

                {/* Componente de upload para fotos Posterior */}
                <ImageUploadManager
                  tipo="despues"
                  maxImages={4}
                  required={false}
                  onImagesChange={handleImagenesDespuesChange}
                  mantenimientoId={null} // Se asignará después de crear el mantenimiento
                  empresaId={empresaActual?.id}
                  sucursalId={sucursalData?.id}
                  disabled={isSubmitting || uploadingImagenes}
                />

                {/* Progreso de subida */}
                {uploadingImagenes && (
                  <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                    <div className="flex items-center text-green-300">
                      <FaSpinner className="animate-spin mr-2" />
                      <span className="text-sm">Subiendo fotos del resultado...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Resumen de Costos */}
          <div className="bg-gray-700/50 border border-gray-600/30 rounded-xl p-5 mt-2">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <span className="inline-block w-1.5 h-5 bg-green-500 rounded-full mr-2"></span>
              Resumen de Costos
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Subtotal Servicios:</span>
                <span className="text-white font-medium">
                  ${formData.serviciosRealizados.reduce((sum, s) => sum + (Number(s.precio) || 0), 0).toLocaleString()}
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Subtotal Repuestos:</span>
                <span className="text-white font-medium">
                  ${formData.repuestosUtilizados.reduce((sum, r) => sum + ((Number(r.precioUnitario) || 0) * (Number(r.cantidad) || 0)), 0).toLocaleString()}
                </span>
              </div>
              
              {formData.itemsAdicionales.length > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-300">Otros conceptos:</span>
                  <span className="text-white font-medium">
                    ${formData.itemsAdicionales.reduce((sum, i) => sum + ((Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 1)), 0).toLocaleString()}
                  </span>
                </div>
              )}
              
              <div className="border-t border-gray-600/30 my-2"></div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">Subtotal:</span>
                <span className="text-white font-medium">
                  ${(
                    formData.serviciosRealizados.reduce((sum, s) => sum + (Number(s.precio) || 0), 0) +
                    formData.repuestosUtilizados.reduce((sum, r) => sum + ((Number(r.precioUnitario) || 0) * (Number(r.cantidad) || 0)), 0) +
                    formData.itemsAdicionales.reduce((sum, i) => sum + ((Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 1)), 0)
                  ).toLocaleString()}
                </span>
              </div>
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-300">IVA ({ivaPorcentaje}%):</span>
                <span className="text-white font-medium">
                  ${(
                    (formData.serviciosRealizados.reduce((sum, s) => sum + (Number(s.precio) || 0), 0) +
                    formData.repuestosUtilizados.reduce((sum, r) => sum + ((Number(r.precioUnitario) || 0) * (Number(r.cantidad) || 0)), 0) +
                    formData.itemsAdicionales.reduce((sum, i) => sum + ((Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 1)), 0)) * (ivaPorcentaje / 100)
                  ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
              
              <div className="border-t border-gray-600/30 my-2"></div>
              
              <div className="flex justify-between text-base">
                <span className="text-gray-100 font-semibold">Total:</span>
                <span className="text-green-400 font-bold">
                  ${(
                    (formData.serviciosRealizados.reduce((sum, s) => sum + (Number(s.precio) || 0), 0) +
                    formData.repuestosUtilizados.reduce((sum, r) => sum + ((Number(r.precioUnitario) || 0) * (Number(r.cantidad) || 0)), 0) +
                    formData.itemsAdicionales.reduce((sum, i) => sum + ((Number(i.precio_unitario) || 0) * (Number(i.cantidad) || 1)), 0)) * (1 + (ivaPorcentaje / 100))
                  ).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>
          </div>
        </form>
      </Modal>
      
      {/* Modal de pago para upgrade de plan */}
      {showPaymentModal && (
        <PaymentModal 
          isOpen={showPaymentModal}
          onClose={closePaymentModal}
          targetPlanId={targetPlanId}
        />
      )}

    </>
  );
};

export default FinalizarMantenimientoModal;
